import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { apiClient, handleApiError } from '@/config/api';
import { NotificationItem } from '@/types/api';

interface NotificationsState {
  items: NotificationItem[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: NotificationsState = {
  items: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
};

export const getNotifications = createAsyncThunk(
  'notifications/list',
  async (_, { rejectWithValue }) => {
    try {
      return await apiClient.get<NotificationItem[]>('/notifications');
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const getUnreadCount = createAsyncThunk(
  'notifications/unreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.get<{ count: number }>('/notifications/unread-count');
      return res.count ?? 0;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  'notifications/markRead',
  async (id: number, { rejectWithValue }) => {
    try {
      await apiClient.patch(`/notifications/${id}/read`);
      return id;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  'notifications/markAllRead',
  async (_, { rejectWithValue }) => {
    try {
      await apiClient.post('/notifications/read-all');
      return true;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getNotifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
        // Don't derive unreadCount here — the list is capped at the top 50, so a
        // user with >50 unread would see it undercount. `/unread-count` (getUnreadCount)
        // is the authoritative source; the screen fetches both on focus.
      })
      .addCase(getNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const item = state.items.find((n) => n.id === action.payload);
        if (item && !item.read) {
          item.read = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.items.forEach((n) => {
          n.read = true;
        });
        state.unreadCount = 0;
      });
  },
});

export default notificationsSlice.reducer;
